/**
 * HomePage - LUKHAS Team Portal
 *
 * Sophisticated commercial design with depth, navy tones, and glass morphism.
 */

import { useState } from 'react'
import {
  Terminal,
  Activity,
  MessageSquare,
  User,
  Home,
  Settings,
  Server,
  Calendar as CalendarIcon,
  CheckSquare,
  Video,
  Clock,
  Bell,
  Book,
  FileText,
  Users,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Search,
  Command,
} from 'lucide-react'
import LukhasTerminal from '../components/LukhasTerminal'
import LiveTestStatus from '../components/LiveTestStatus'
import StaffProfile from '../components/StaffProfile'
import InternalMessaging from '../components/InternalMessaging'
import HRSection from '../components/HRSection'
import TranslationManager from '../components/admin/TranslationManager'
import SystemStatus from '../components/admin/SystemStatus'
import TeamManager from '../components/admin/TeamManager'
import StaffOnboarding from '../components/admin/StaffOnboarding'
import Calendar from '../components/Calendar'
import Tasks from '../components/Tasks'
import MeetingRoom from '../components/MeetingRoom'
import DocsLibrary from '../components/DocsLibrary'
import ReportsBoard from '../components/ReportsBoard'
import Screensaver from '../components/Screensaver'
import SettingsModal from '../components/SettingsModal'
import { useSettingsStore } from '../stores/settingsStore'
import { cn } from '../lib/utils'

type TabId = 'home' | 'terminal' | 'tests' | 'messages' | 'profile' | 'hr' | 'translations' | 'system' | 'calendar' | 'tasks' | 'meeting' | 'docs' | 'reports' | 'team' | 'onboarding'

const navItems = [
  { id: 'home' as const, icon: Home, label: 'Home' },
  { id: 'calendar' as const, icon: CalendarIcon, label: 'Calendar' },
  { id: 'tasks' as const, icon: CheckSquare, badge: 4, label: 'Tasks' },
  { id: 'meeting' as const, icon: Video, label: 'Meetings' },
  { id: 'docs' as const, icon: Book, label: 'Docs' },
  { id: 'reports' as const, icon: FileText, label: 'Reports' },
  { id: 'terminal' as const, icon: Terminal, label: 'Terminal' },
  { id: 'tests' as const, icon: Activity, label: 'Tests' },
  { id: 'messages' as const, icon: MessageSquare, badge: 3, label: 'Messages' },
  { id: 'team' as const, icon: Users, label: 'Team' },
  { id: 'system' as const, icon: Server, label: 'System' },
]

const bottomItems = [
  { id: 'profile' as const, icon: User, label: 'Profile' },
]

// Mock data
const UPCOMING_TASKS = [
  { id: '1', title: 'Review consciousness module docs', dueDate: 'Today', priority: 'high' as const },
  { id: '2', title: 'Fix Guardian latency issue', dueDate: 'Tomorrow', priority: 'high' as const, reminder: '2h before' },
]

const UPCOMING_MEETINGS = [
  { id: '1', title: 'Team standup', time: '09:00', attendees: 4 },
  { id: '2', title: 'Sprint planning', time: '10:00', attendees: 8 },
]

export default function HomePage() {
  const [activeTab, setActiveTab] = useState<TabId>('home')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { showIconLabels } = useSettingsStore()

  return (
    <div
      className="min-h-screen flex"
      style={{ backgroundColor: 'var(--color-bg)' }}
    >
      <Screensaver />
      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />

      {/* Sidebar - Glass morphism with depth */}
      <aside
        className={cn(
          'flex flex-col py-4 transition-all duration-300 relative',
          showIconLabels ? 'w-52' : 'w-16'
        )}
        style={{
          background: 'linear-gradient(180deg, var(--color-bg-secondary) 0%, var(--color-bg) 100%)',
          borderRight: '1px solid var(--color-border-subtle)',
        }}
      >
        {/* Subtle glow effect at top */}
        <div
          className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at top, var(--color-accent-muted) 0%, transparent 70%)',
          }}
        />

        {/* Logo */}
        <div className={cn('px-3 mb-6 relative', showIconLabels ? 'px-4' : 'flex justify-center')}>
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, var(--color-accent) 0%, var(--color-highlight) 100%)',
                boxShadow: '0 4px 12px var(--color-accent-muted)',
              }}
            >
              <span className="font-bold text-sm text-white">L</span>
            </div>
            {showIconLabels && (
              <div>
                <span className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>
                  LUKHAS
                </span>
                <span
                  className="block text-[10px] uppercase tracking-wider"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Team Portal
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Search - Only when expanded */}
        {showIconLabels && (
          <div className="px-4 mb-4">
            <button
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-muted)',
                border: '1px solid var(--color-border-subtle)',
              }}
            >
              <Search className="w-4 h-4" />
              <span className="flex-1 text-left">Search...</span>
              <kbd
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <Command className="w-3 h-3 inline" />K
              </kbd>
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className={cn(
          'flex-1 flex flex-col gap-0.5 overflow-y-auto px-2',
          !showIconLabels && 'items-center'
        )}>
          {navItems.map(({ id, icon: Icon, badge, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                'relative rounded-lg flex items-center gap-3 transition-all duration-200 group',
                showIconLabels ? 'w-full px-3 py-2.5' : 'w-10 h-10 justify-center',
              )}
              style={{
                backgroundColor: activeTab === id ? 'var(--color-accent-muted)' : 'transparent',
                color: activeTab === id ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              }}
            >
              {/* Active indicator */}
              {activeTab === id && (
                <div
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-full"
                  style={{ backgroundColor: 'var(--color-accent)' }}
                />
              )}
              <Icon
                className={cn('w-[18px] h-[18px] flex-shrink-0 transition-colors', !showIconLabels && 'w-5 h-5')}
                style={{
                  color: activeTab === id ? 'var(--color-accent)' : undefined,
                }}
              />
              {showIconLabels && (
                <span className="text-[13px] font-medium truncate">{label}</span>
              )}
              {badge && (
                <span
                  className={cn(
                    'text-[10px] font-semibold rounded-full flex items-center justify-center',
                    showIconLabels ? 'ml-auto w-5 h-5' : 'absolute -top-0.5 -right-0.5 w-4 h-4'
                  )}
                  style={{
                    backgroundColor: 'var(--color-accent)',
                    color: 'var(--color-bg)',
                  }}
                >
                  {badge}
                </span>
              )}

              {/* Hover tooltip for collapsed */}
              {!showIconLabels && (
                <div
                  className="absolute left-full ml-2 px-2 py-1 rounded-md text-xs font-medium opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50"
                  style={{
                    backgroundColor: 'var(--color-bg-tertiary)',
                    color: 'var(--color-text-primary)',
                    border: '1px solid var(--color-border)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  {label}
                </div>
              )}
            </button>
          ))}
        </nav>

        {/* Divider */}
        <div className="mx-3 my-3" style={{ borderTop: '1px solid var(--color-border-subtle)' }} />

        {/* Bottom */}
        <div className={cn('flex flex-col gap-0.5 px-2', !showIconLabels && 'items-center')}>
          {bottomItems.map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                'relative rounded-lg flex items-center gap-3 transition-all duration-200 group',
                showIconLabels ? 'w-full px-3 py-2.5' : 'w-10 h-10 justify-center',
              )}
              style={{
                backgroundColor: activeTab === id ? 'var(--color-accent-muted)' : 'transparent',
                color: activeTab === id ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              }}
            >
              <Icon className={cn('w-[18px] h-[18px] flex-shrink-0', !showIconLabels && 'w-5 h-5')} />
              {showIconLabels && <span className="text-[13px] font-medium">{label}</span>}

              {!showIconLabels && (
                <div
                  className="absolute left-full ml-2 px-2 py-1 rounded-md text-xs font-medium opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50"
                  style={{
                    backgroundColor: 'var(--color-bg-tertiary)',
                    color: 'var(--color-text-primary)',
                    border: '1px solid var(--color-border)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  {label}
                </div>
              )}
            </button>
          ))}

          {/* Settings Button */}
          <button
            onClick={() => setSettingsOpen(true)}
            className={cn(
              'relative rounded-lg flex items-center gap-3 transition-all duration-200 group',
              showIconLabels ? 'w-full px-3 py-2.5' : 'w-10 h-10 justify-center',
            )}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <Settings className={cn('w-[18px] h-[18px] flex-shrink-0', !showIconLabels && 'w-5 h-5')} />
            {showIconLabels && <span className="text-[13px] font-medium">Settings</span>}

            {!showIconLabels && (
              <div
                className="absolute left-full ml-2 px-2 py-1 rounded-md text-xs font-medium opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                Settings
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className="flex-1 overflow-y-auto"
        style={{ backgroundColor: 'var(--color-bg)' }}
      >
        <div className={cn(
          'mx-auto py-8',
          activeTab === 'docs' ? 'max-w-none px-0' : 'max-w-6xl px-8'
        )}>
          <Content activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
      </main>
    </div>
  )
}

function Content({ activeTab, setActiveTab }: { activeTab: TabId; setActiveTab: (t: TabId) => void }) {
  switch (activeTab) {
    case 'calendar':
      return <Calendar />
    case 'tasks':
      return <Tasks />
    case 'meeting':
      return <MeetingRoom />
    case 'docs':
      return <DocsLibrary />
    case 'reports':
      return <ReportsBoard />
    case 'team':
      return <TeamManager />
    case 'onboarding':
      return <StaffOnboarding />
    case 'terminal':
      return (
        <div>
          <PageHeader title="Terminal" subtitle="LUKHAS development sandbox" />
          <LukhasTerminal className="h-[600px]" />
        </div>
      )
    case 'tests':
      return (
        <div>
          <PageHeader title="Test Status" subtitle="Live test runner and coverage" />
          <LiveTestStatus />
        </div>
      )
    case 'messages':
      return (
        <div>
          <PageHeader title="Messages" subtitle="Team conversations and threads" />
          <InternalMessaging />
        </div>
      )
    case 'profile':
      return (
        <div>
          <PageHeader title="Profile" subtitle="Your account and preferences" />
          <StaffProfile />
        </div>
      )
    case 'hr':
      return (
        <div>
          <PageHeader title="HR Center" subtitle="Human resources and policies" />
          <HRSection />
        </div>
      )
    case 'translations':
      return <TranslationManager />
    case 'system':
      return <SystemStatus />
    default:
      return <Dashboard setActiveTab={setActiveTab} />
  }
}

function PageHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-8">
      <h1
        className="text-2xl font-semibold tracking-tight"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {title}
      </h1>
      {subtitle && (
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {subtitle}
        </p>
      )}
    </div>
  )
}

function Dashboard({ setActiveTab }: { setActiveTab: (t: TabId) => void }) {
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const today = new Date()

  return (
    <div className="space-y-8">
      {/* Header with greeting and time */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1
              className="text-3xl font-semibold tracking-tight"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {greeting}, Alex
            </h1>
            <Sparkles className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
          </div>
          <p style={{ color: 'var(--color-text-muted)' }}>
            {today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div
          className="px-4 py-2 rounded-xl"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-subtle)',
          }}
        >
          <p
            className="text-3xl font-light tabular-nums"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {today.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Active Projects', value: '7', trend: '+2', icon: TrendingUp },
          { label: 'Team Online', value: '18', trend: null, icon: Users },
          { label: 'Coverage', value: '89%', trend: '+3%', icon: Activity },
          { label: 'Open PRs', value: '12', trend: '-4', icon: FileText },
        ].map(({ label, value, trend, icon: Icon }) => (
          <div
            key={label}
            className="p-4 rounded-xl transition-all duration-200 hover:translate-y-[-2px]"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-subtle)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-accent-muted)' }}
              >
                <Icon className="w-4 h-4" style={{ color: 'var(--color-accent)' }} />
              </div>
              {trend && (
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: trend.startsWith('+') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    color: trend.startsWith('+') ? 'var(--color-success)' : 'var(--color-error)',
                  }}
                >
                  {trend}
                </span>
              )}
            </div>
            <p
              className="text-2xl font-semibold"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {value}
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              {label}
            </p>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Tasks & Meetings */}
        <div className="col-span-2 space-y-6">
          {/* Task Reminders */}
          {UPCOMING_TASKS.length > 0 && (
            <div
              className="p-5 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-accent-muted)' }}
                  >
                    <Bell className="w-3.5 h-3.5" style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <span className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>
                    Needs Attention
                  </span>
                </div>
                <button
                  onClick={() => setActiveTab('tasks')}
                  className="flex items-center gap-1 text-xs font-medium transition-colors hover:opacity-80"
                  style={{ color: 'var(--color-accent)' }}
                >
                  View all <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="space-y-2">
                {UPCOMING_TASKS.map(task => (
                  <div
                    key={task.id}
                    className="flex items-center gap-3 p-3 rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                  >
                    <span
                      className={cn(
                        'w-2 h-2 rounded-full flex-shrink-0',
                        task.priority === 'high' ? 'bg-red-500' : 'bg-yellow-500'
                      )}
                    />
                    <span className="flex-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                      {task.title}
                    </span>
                    <span
                      className="text-xs flex items-center gap-1"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      <Clock className="w-3 h-3" />
                      {task.dueDate}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Meetings */}
          {UPCOMING_MEETINGS.length > 0 && (
            <div
              className="p-5 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-subtle)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-accent-muted)' }}
                  >
                    <Video className="w-3.5 h-3.5" style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <span className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>
                    Today's Meetings
                  </span>
                </div>
                <button
                  onClick={() => setActiveTab('calendar')}
                  className="flex items-center gap-1 text-xs font-medium transition-colors hover:opacity-80"
                  style={{ color: 'var(--color-accent)' }}
                >
                  Calendar <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="space-y-2">
                {UPCOMING_MEETINGS.map(meeting => (
                  <button
                    key={meeting.id}
                    onClick={() => setActiveTab('meeting')}
                    className="w-full flex items-center gap-4 p-3 rounded-lg transition-all hover:scale-[1.01]"
                    style={{
                      backgroundColor: 'var(--color-bg-tertiary)',
                      border: '1px solid transparent',
                    }}
                  >
                    <span
                      className="text-xl font-semibold tabular-nums"
                      style={{ color: 'var(--color-accent)' }}
                    >
                      {meeting.time}
                    </span>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {meeting.title}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        {meeting.attendees} attendees
                      </p>
                    </div>
                    <div
                      className="px-2.5 py-1 rounded-md text-xs font-medium"
                      style={{
                        backgroundColor: 'var(--color-accent-muted)',
                        color: 'var(--color-accent)',
                      }}
                    >
                      Join
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Quick Access */}
        <div className="space-y-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
            Quick Access
          </h3>
          <div className="space-y-2">
            {[
              { id: 'docs' as const, icon: Book, label: 'Documentation', desc: 'Internal docs library' },
              { id: 'terminal' as const, icon: Terminal, label: 'Terminal', desc: 'LUKHAS sandbox' },
              { id: 'team' as const, icon: Users, label: 'Team', desc: '5 members online', online: true },
              { id: 'system' as const, icon: Server, label: 'System', desc: 'All operational', status: 'ok' },
              { id: 'messages' as const, icon: MessageSquare, label: 'Messages', desc: '3 unread', badge: 3 },
            ].map(({ id, icon: Icon, label, desc, online, status, badge }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all duration-200 group hover:translate-x-1"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-subtle)',
                }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors group-hover:scale-105"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                >
                  <Icon className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                    {label}
                  </p>
                  <div className="flex items-center gap-1.5">
                    {online && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                    {status === 'ok' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                    <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                      {desc}
                    </p>
                  </div>
                </div>
                {badge && (
                  <span
                    className="w-5 h-5 text-[10px] font-semibold rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: 'var(--color-accent)',
                      color: 'var(--color-bg)',
                    }}
                  >
                    {badge}
                  </span>
                )}
                <ChevronRight
                  className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: 'var(--color-text-muted)' }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
